import { KeyboardEvent, useState } from 'react';
import { useMutation } from '@apollo/client';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

import {
  CreateGachaCampaignMutation,
  CreateGachaCampaignMutationVariables,
  DeleteGachaCampaignMutation,
  DeleteGachaCampaignMutationVariables,
  SetGachaCampaignOrderMutation,
  SetGachaCampaignOrderMutationVariables,
  UpdateGachaCampaignMutation,
  UpdateGachaCampaignMutationVariables,
} from '../../../../../../__gqltypes__';
import {
  CREATE_GACHA_CAMPAIGN,
  DELETE_GACHA_CAMPAIGN,
  SET_GACHA_CAMPAIGN_ORDER,
  UPDATE_GACHA_CAMPAIGN,
} from '../graphql';
import {
  formatUtc,
  GACHA_STATUS_VARIANT,
  GachaCampaignData,
  isGachaTestBackend,
  parseGachaAdminError,
  reorderList,
  toGachaCampaignDraft,
  toUpdateGachaCampaignInput,
  withGachaNewCampaignDefaults,
} from '../utils';

type Props = {
  // Already in carousel order (displayOrder, then creation), as the server returns them.
  campaigns: GachaCampaignData[];
  selectedId: string | null;
  pinnedCampaignId: string | null;
  onSelect: (id: string) => void;
  onCampaignsChange: (campaigns: GachaCampaignData[]) => void;
  onCreated: (campaign: GachaCampaignData) => void;
  onDeleted: (id: string) => void;
};

const MAX_NAME_LENGTH = 128;

/**
 * Campaign sidebar: carousel order (level 1), drag to reorder (save-on-drop sends the full id list,
 * the curation SongList pattern), server status badges, create by name, delete when deletable.
 */
export function GachaCampaignList({
  campaigns,
  selectedId,
  pinnedCampaignId,
  onSelect,
  onCampaignsChange,
  onCreated,
  onDeleted,
}: Props): JSX.Element {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [toDelete, setToDelete] = useState<GachaCampaignData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createCampaign, { loading: creating }] = useMutation<
    CreateGachaCampaignMutation,
    CreateGachaCampaignMutationVariables
  >(CREATE_GACHA_CAMPAIGN);
  const [updateCampaign, { loading: applyingDefaults }] = useMutation<
    UpdateGachaCampaignMutation,
    UpdateGachaCampaignMutationVariables
  >(UPDATE_GACHA_CAMPAIGN);
  const [setOrder, { loading: reordering }] = useMutation<
    SetGachaCampaignOrderMutation,
    SetGachaCampaignOrderMutationVariables
  >(SET_GACHA_CAMPAIGN_ORDER);
  const [deleteCampaign, { loading: deleting }] = useMutation<
    DeleteGachaCampaignMutation,
    DeleteGachaCampaignMutationVariables
  >(DELETE_GACHA_CAMPAIGN);

  const trimmedName = newName.trim();
  const testBackend = isGachaTestBackend();
  const canCreate = trimmedName.length > 0 && trimmedName.length <= MAX_NAME_LENGTH && !creating && !applyingDefaults;

  // The server creates the campaign starting now, permanent, start push on. Right after the creation the admin
  // saves the new-campaign defaults over it: a future start, an end date, and the start push explicitly off. The
  // campaign is disabled, so this save needs no running-edit confirmation.
  const applyNewCampaignDefaults = (created: GachaCampaignData): Promise<GachaCampaignData> =>
    updateCampaign({
      variables: {
        input: toUpdateGachaCampaignInput(
          withGachaNewCampaignDefaults(toGachaCampaignDraft(created), Date.now(), testBackend)
        ),
        expectedConfigVersion: created.configVersion,
        confirmRunningEdit: false,
      },
    })
      .then(({ data }) => data?.updateGachaCampaign ?? created)
      .catch((error) => {
        setErrorMessage(
          `The campaign was created, but its default dates and start push could not be saved (${error.message}). ` +
            'It starts now, is permanent and has its start push on: set them in the editor before enabling it.'
        );
        return created;
      });

  const handleCreate = () => {
    if (!canCreate) {
      return;
    }
    setErrorMessage(null);
    createCampaign({ variables: { name: trimmedName } })
      .then(async ({ data }) => {
        if (data?.createGachaCampaign) {
          onCreated(await applyNewCampaignDefaults(data.createGachaCampaign));
        }
        setShowCreate(false);
        setNewName('');
      })
      .catch((error) => setErrorMessage(error.message));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    const previous = campaigns;
    const reordered = reorderList(campaigns, result.source.index, result.destination.index);
    onCampaignsChange(reordered);
    setErrorMessage(null);
    setOrder({ variables: { ids: reordered.map((campaign) => campaign.id) } })
      .then(({ data }) => {
        if (data?.setGachaCampaignOrder) {
          onCampaignsChange(data.setGachaCampaignOrder);
        }
      })
      .catch((error) => {
        onCampaignsChange(previous);
        setErrorMessage(`The new order was not saved: ${error.message}`);
      });
  };

  const handleDelete = () => {
    if (!toDelete) {
      return;
    }
    const { id } = toDelete;
    setErrorMessage(null);
    deleteCampaign({ variables: { id } })
      .then(() => onDeleted(id))
      .catch((error) => {
        const { code } = parseGachaAdminError(error.message);
        setErrorMessage(
          code === 'GACHA_ADMIN_NOT_DELETABLE'
            ? 'This campaign cannot be deleted: it has run (players have state for it, or its start push went ' +
                'out). Disable it instead.'
            : error.message
        );
      })
      .finally(() => setToDelete(null));
  };

  return (
    <div>
      <Button className="w-100 mb-2" variant="success" onClick={() => setShowCreate(true)} disabled={creating}>
        <i className="fas fa-plus mr-2" />
        Create a campaign
      </Button>
      <div className="small text-muted mb-2">
        Drag to reorder: this is the carousel order for players (boxes of the first campaign come first).
        {reordering && <Spinner animation="border" size="sm" className="ml-2" />}
      </div>
      {errorMessage && (
        <Alert variant="danger" dismissible onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}
      {campaigns.length === 0 && <div className="text-muted text-center my-3">No campaign yet.</div>}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="gacha-campaigns">
          {(droppable) => (
            <div className="list-group" {...droppable.droppableProps} ref={droppable.innerRef}>
              {campaigns.map((campaign, index) => (
                <Draggable key={campaign.id} draggableId={campaign.id} index={index} isDragDisabled={reordering}>
                  {(draggable) => (
                    <div
                      ref={draggable.innerRef}
                      {...draggable.draggableProps}
                      {...draggable.dragHandleProps}
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelect(campaign.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          onSelect(campaign.id);
                        }
                      }}
                      className={`list-group-item list-group-item-action py-2 ${
                        campaign.id === selectedId ? 'active' : ''
                      }`}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="mr-2" style={{ minWidth: 0 }}>
                          <div className="font-weight-bold text-truncate" title={campaign.name}>
                            <span className="text-muted mr-1">{index + 1}.</span>
                            {campaign.name}
                            {campaign.id === pinnedCampaignId && (
                              <i className="fas fa-star text-warning ml-1" title="Holds the pinned default box" />
                            )}
                          </div>
                          <div>
                            <Badge variant={GACHA_STATUS_VARIANT[campaign.status]} className="mr-1">
                              {campaign.status}
                            </Badge>
                            {campaign.permanent && (
                              <Badge variant="light" className="mr-1">
                                Permanent
                              </Badge>
                            )}
                            <Badge variant="light">{campaign.namespace}</Badge>
                          </div>
                          <div className="small">
                            {formatUtc(campaign.startDate)}
                            {!campaign.permanent && (
                              <>
                                <br />→ {formatUtc(campaign.stopDate)}
                              </>
                            )}
                          </div>
                        </div>
                        {campaign.deletable && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            title="Delete (only a campaign that never ran)"
                            onClick={(e) => {
                              e.stopPropagation();
                              setToDelete(campaign);
                            }}
                          >
                            <i className="fas fa-trash" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {droppable.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Modal show={showCreate} onHide={() => setShowCreate(false)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Create a Gacha campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label htmlFor="gacha-new-campaign-name">Name</Form.Label>
          <Form.Control
            id="gacha-new-campaign-name"
            value={newName}
            maxLength={MAX_NAME_LENGTH}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
              if (e.key === 'Enter') {
                handleCreate();
              }
            }}
            autoFocus
          />
          <Form.Text className="text-muted">
            The campaign is created disabled, with no box, starting in {testBackend ? '15 minutes' : '1 hour'} and
            ending {testBackend ? '7 hours' : '7 days'} later, with its start push off. Configure it, then enable it.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleCreate} disabled={!canCreate}>
            {creating || applyingDefaults ? <Spinner animation="border" size="sm" /> : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={toDelete !== null} onHide={() => setToDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Delete this campaign?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{toDelete?.name}</strong> and its boxes are deleted permanently.
          </p>
          <p className="mb-0 text-muted">
            Only a campaign that never ran can be deleted. The server refuses it when any player already has state for
            it.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
